import { create, type StoreApi } from 'zustand';
import { devtools } from 'zustand/middleware';
import { SUPPORTED_CURRENCIES, type CurrencyCode, type Product, type DiscountType } from '@bbpos/types';
import { toBillMode, type BillMode } from '@bbpos/ui/src/components/POSBillingComponents/BillModeSelector/types';
import type { CustomerLookupResult } from '@bbpos/ui/src/components/POSBillingComponents/CustomerLookup/types';
import type { ParkedBill } from '@bbpos/ui/src/components/POSBillingComponents/ParkBillPanel/types';
import { offlineBillingService } from '@/services/offline-billing.service';
import { billingService } from '@/services/billing.service';
import type { BillWithItems, ServerParkedBill } from '@/services/billing.service';
import {
  discountIsOnServer,
  isOfflineParkError,
  isUploadableParkedDraft,
  parkNote,
  parkOnServer,
  pushLocalParkedBills,
  type ParkableCart,
} from '@/services/parked-bills.service';
import { useLocationStore } from '@/stores/use-location-store';
import { useNetworkStore } from '@/stores/use-network-store';
import type { DraftBill, DraftBillDiscount, DraftPendingDiscountApproval } from '@/db/pos-database';
import { computeLineGst, gstWaived, roundBillTotal, setGstRegistration } from '@/lib/gst';
import { cashRoundingRuleFor } from '@/lib/cash-rounding';

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * `BillDiscount.appliedBy` marker for a discount reconstructed from a server
 * DRAFT's `discount_total` by `hydrateFromServerBill`. GET /bills/:id only
 * exposes the collapsed paise figure (type/value/reason are not persisted),
 * so checkout uses this marker to know the server already holds it.
 */
export const RESUMED_DISCOUNT_APPLIED_BY = 'resume';

/**
 * `BillDiscount.appliedBy` marker for a discount a manager approved. Like a
 * resumed one, it is ALREADY on the server — the approval consumer applied it —
 * so checkout must not post it again. Re-posting sent the full value back
 * through the role cap and failed the payment with a 403.
 */
export const APPROVED_DISCOUNT_APPLIED_BY = 'approval';

export interface CartLineItem {
  id: string;
  productId: string;
  name: string;
  sku: string;
  unit: string;
  quantity: number;
  /** Snapshot of price at time of adding, in paise */
  unitPrice: number;
  /**
   * Catalogue price when the line was added, before modifier surcharges. Sent
   * as expected_unit_price so the server can flag a price changed since then
   * (it prices the bill itself). Absent on lines hydrated from a server draft.
   */
  listPrice?: number;
  /** Cost at time of adding (WAC), in paise */
  unitCost: number;
  /** GST rate as percentage (e.g. 18) */
  gstRate: number;
  hsnCode?: string;
  /** Display-only add-on names, e.g. "Extra Cheese, Extra Gravy". The add-on
   *  price is folded into unitPrice, not tracked separately here. */
  modifiers?: string;
  /**
   * Line came from a server DRAFT (hydrateFromServerBill). The server only
   * keeps a collapsed paise discount per line, so a member's tier discount
   * arrives as FLAT; `setMembership` uses this flag to turn such lines back
   * into live tier discounts (see there). Never persisted.
   */
  fromServer?: boolean;
  discountType: DiscountType | null;
  /** Discount value — percentage (0-100) or flat amount in paise */
  discountValue: number;
  /** Computed: discount amount in paise */
  discountAmount: number;
  /** Computed: unitPrice * quantity, in paise */
  lineSubtotal: number;
  /** Computed: CGST in paise (intra-state) */
  cgst: number;
  /** Computed: SGST in paise (intra-state) */
  sgst: number;
  /** Computed: IGST in paise (inter-state) */
  igst: number;
  /** Computed: total tax in paise */
  taxAmount: number;
  /** Computed: lineSubtotal - discountAmount + taxAmount */
  lineTotal: number;
  /**
   * Serial numbers scanned for this line, one per unit. Only populated for
   * SERIAL-tracked products; checkout is blocked until `length === quantity`.
   * Validated against stock as each is scanned, and again server-side at
   * completion.
   */
  serials?: string[];
}

/**
 * Bill-level discount carried on the active cart. Separate from per-line
 * discounts (those live on `CartLineItem.discountType/discountValue`).
 * Gets pro-rated across lines by `useBillCalculations` so per-line GST
 * stays floor-correct (CLAUDE.md §GST).
 */
export interface BillDiscount {
  type: DiscountType;
  /** Paise for FLAT, whole percent 0..100 for PERCENTAGE */
  value: number;
  /** Required for audit; min 3 chars enforced by the modal */
  reason: string;
  /** User id from auth store */
  appliedBy: string;
  /** Date.now() at time of application */
  appliedAt: number;
  /**
   * When set, the discount originated from a voucher code. Checkout will
   * call `POST /vouchers/:code/redeem` (which both applies the discount
   * server-side and increments used_count) instead of the manual
   * `applyDiscount` path.
   */
  voucherCode?: string;
  /**
   * C03: a voucher for some items only. Only these products' lines share the
   * discount; the others get none. Unset = every line (as before).
   */
  eligibleProductIds?: string[];
  /** The voucher's scope in words ("Snacks"), for the "removed" message. */
  scopeLabel?: string;
}

/** Does this line share the bill discount? Every line does, unless a scoped voucher names the products. */
export function sharesBillDiscount(productId: string, billDiscount: BillDiscount | null): boolean {
  return !billDiscount?.eligibleProductIds || billDiscount.eligibleProductIds.includes(productId);
}

/**
 * Active membership attached to the current bill. When present, the tier's
 * `discountPct` is auto-applied per line (only to lines without a manual
 * discount) — mirroring the backend's createBill auto-apply so the on-screen
 * total matches the stored bill exactly. Not a manual `BillDiscount`, so it
 * bypasses the discount-approval caps.
 */
export interface CartMembership {
  planName: string;
  tier: string;
  /** Tier discount, whole percent 0..100. */
  discountPct: number;
}

/**
 * #308 — Recipe-based availability for one product (RESTAURANT vertical):
 * how many portions can still be prepared from current ingredient stock.
 * Populated by the billing page from GET /products/availability; products
 * with no entry are not recipe-limited and are never blocked.
 */
export interface IngredientAvailability {
  available: number;
  limitingIngredient: string;
}

/**
 * FR-BILL-050: Pending discount approval state — tracks an in-flight
 * approval request while the cashier waits for a Manager/Owner response.
 */
export interface PendingDiscountApproval {
  requestId: string;
  type: 'PERCENTAGE' | 'FLAT';
  value: number;
  reason: string;
  requestedAt: number;
}

interface CartState {
  items: CartLineItem[];
  billMode: BillMode;
  selectedCustomer: CustomerLookupResult | null;
  parkedBills: ParkedBill[];
  currentBillId: string | null;
  currentDraftId: string;
  /** The split this cart shows: the store's supply type unless the cashier switched it. */
  isInterState: boolean;
  /**
   * gst-parked D7: the cashier's per-bill IGST switch, sent as `supply_override`.
   * null = the store's own supply type (the GST resolver on /auth/me), which
   * the server decides the same way — nothing is sent.
   */
  supplyOverride: SupplyOverride | null;
  /** A manager's TAX_SWITCH approval for a cashier's switch (single use, never saved). */
  supplyOverrideApprovalToken: string | null;
  isHydrated: boolean;
  billDiscount: BillDiscount | null;
  /** FR-BILL-050: non-null when a discount approval request is in flight. */
  pendingDiscountApproval: PendingDiscountApproval | null;
  /** Active membership for the attached customer; drives the auto tier discount. */
  membership: CartMembership | null;
  /**
   * Set while the cart holds an existing quote opened for editing (Quote
   * detail → Edit). Saving then updates THAT quote instead of creating one.
   * Any action that replaces the cart clears it.
   */
  editingQuote: { id: string; quoteNumber: string; locationId: string } | null;
  /**
   * Set while the cart bills an open quote (Quote detail → Bill this quote,
   * T10). Completion sends its id so the server closes and links the quote,
   * and its down payment is deducted from what is collected.
   */
  billingQuote: { id: string; quoteNumber: string; advancePaid: number } | null;
  /**
   * Resumed EXCHANGE draft: the return's credit, which the server applies
   * before the tender. Checkout collects only what is left.
   */
  exchangeCredit: { amount: number; creditNoteNumber: string } | null;
  /** #308: per-product recipe availability; addItem refuses to exceed it. */
  ingredientAvailability: Record<string, IngredientAvailability>;
  /**
   * Line id touched by the most recent successful `addItem` — the appended row,
   * or the existing row whose quantity went up. The cart list uses it to scroll
   * that line into view and flash it, so the cashier can see what they added
   * without scrolling. Set on add only: removals and quantity *decreases* leave
   * it alone, so they never trigger a scroll.
   */
  lastAddedItemId: string | null;

  // Actions
  /** Returns false when the add was blocked by ingredient availability (#308). */
  addItem: (product: Product, quantity?: number) => boolean;
  setIngredientAvailability: (map: Record<string, IngredientAvailability>) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, qty: number) => void;
  /** Replace the scanned serial numbers on a line (one per unit). */
  setLineSerials: (itemId: string, serials: string[]) => void;
  /** Apply add-ons to a line: set its unit price (base + surcharge) and the
   *  display names. Used by the billing add-on panel. */
  setLineModifiers: (itemId: string, unitPrice: number, modifiers?: string) => void;
  /** Move lines to the catalogue's current price, keeping any modifier surcharge. */
  repriceLines: (changes: Array<{ productId: string; price: number }>) => void;
  applyItemDiscount: (itemId: string, type: DiscountType, value: number) => void;
  clearItemDiscount: (itemId: string) => void;
  applyBillDiscount: (discount: Omit<BillDiscount, 'appliedAt'>) => void;
  clearBillDiscount: () => void;
  setPendingDiscountApproval: (approval: PendingDiscountApproval) => void;
  clearPendingDiscountApproval: () => void;
  resolvePendingDiscountApproval: (result: 'approved' | 'rejected', discount?: Omit<BillDiscount, 'appliedAt' | 'appliedBy'>) => void;
  /** Attach the cart to a server DRAFT created before checkout (discount approval). */
  setCurrentBillId: (billId: string | null) => void;
  setCustomer: (customer: CustomerLookupResult | null) => void;
  setMembership: (membership: CartMembership | null) => void;
  setBillMode: (mode: BillMode) => void;
  /** `approvalToken`: the manager's TAX_SWITCH approval when the user may not edit taxes. */
  setIsInterState: (value: boolean, approvalToken?: string) => void;
  clearCart: () => void;
  /**
   * gst-parked 120: park the cart — on the server (the store's list, every
   * till) when online, else on this till until it can upload. Resolves with
   * where it went and its reference (null for an empty cart). Rejects with the
   * server's answer when it refuses the park; the cart is then kept.
   * `promoByLine`: the automatic promotion the cart shows per line (the
   * sidebar's `applyPromoPreview` input), so the parked row lists the same total.
   */
  parkCurrentBill: (promoByLine?: Record<string, number>) => Promise<ParkOutcome | null>;
  /**
   * Restore a bill parked on THIS till (`localState` set) from IndexedDB. A
   * server parked bill resumes through POST /bills/:id/resume + GET /bills/:id
   * and `hydrateFromServerBill` (BillingPage). Resolves with the reference the
   * current cart was auto-parked under, or null.
   */
  resumeBill: (billId: string) => Promise<string | null>;
  /**
   * Load a server-side DRAFT bill (Bill History → Resume) into the cart so
   * checkout completes THAT bill instead of creating a second one. `units`
   * maps product_id → display unit (GET /bills/:id carries none).
   * Resolves with the reference the current cart was auto-parked under, or null.
   */
  hydrateFromServerBill: (bill: BillWithItems, units: Record<string, string>) => Promise<string | null>;
  /**
   * Load an ACTIVE quote into the cart (quotation mode) for editing. `units`
   * maps product_id → display unit. Resolves with the auto-park reference, or null.
   */
  loadQuoteForEdit: (quote: QuoteForEdit, units: Record<string, string>) => Promise<string | null>;
  /**
   * Load an open quote into the cart as a sale (T10). Lines are editable and
   * priced at the catalogue price, which is what the bill charges. Resolves
   * with the auto-park reference, or null.
   */
  loadQuoteForBilling: (quote: QuoteForBilling, units: Record<string, string>) => Promise<string | null>;
  /**
   * Discard a parked bill: a server one is voided (the server decides who may —
   * a cashier only their own), a local one is removed. Rejects with the
   * server's refusal; the list is left as it was.
   */
  discardParkedBill: (billId: string) => Promise<void>;
  /** gst-parked 120: the store's server parked list merged with this till's not-yet-uploaded parks. */
  refreshParkedBills: () => Promise<void>;
  /** gst-parked 120: upload this till's offline parks (when online), then refresh the list. */
  syncParkedBills: () => Promise<void>;
  hydrateFromIndexedDB: () => Promise<void>;
  resetAll: () => void;
}

/** gst-parked 120: where a park went — the store's server list, or this till until it can upload. */
export interface ParkOutcome {
  where: 'server' | 'local';
  /** PKD-YYYYMMDD-NNNN on the server; PARK-<base36> on the till. */
  reference: string;
}

/** gst-parked 120: shown instead of parking while a discount waits for a manager. */
export const PARK_PENDING_APPROVAL_MSG =
  'This bill is waiting for a manager to approve its discount. Park it after the answer.';

/** The GET /quotations/:id fields `loadQuoteForBilling` reads on top of `QuoteForEdit`. */
export interface QuoteForBilling extends QuoteForEdit {
  advance_paid?: number;
  items: Array<QuoteForEdit['items'][number] & { current_price?: number | null }>;
}

/** The GET /quotations/:id fields `loadQuoteForEdit` reads. */
export interface QuoteForEdit {
  id: string;
  quote_number: string;
  location_id: string;
  customer_id: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  items: Array<{
    product_id: string;
    product_name: string;
    product_sku?: string;
    quantity: number;
    unit_price: number;
    gst_rate: number;
    line_discount?: number;
  }>;
}

// ─── Bill-discount serialization helpers ─────────────────────────────────────
//
// `BillDiscount` and `DraftBillDiscount` share the same camelCase shape, so
// the helpers just clone the value (avoiding accidental shared references
// between in-memory state and the IndexedDB record) and coerce nullables.

function toDraftDiscount(d: BillDiscount | null): DraftBillDiscount | null {
  return d ? { ...d } : null;
}

function fromDraftDiscount(d: DraftBillDiscount | null | undefined): BillDiscount | null {
  return d ? { ...d } : null;
}

function toDraftPendingApproval(p: PendingDiscountApproval | null): DraftPendingDiscountApproval | null {
  return p ? { ...p } : null;
}

function fromDraftPendingApproval(p: DraftPendingDiscountApproval | null | undefined): PendingDiscountApproval | null {
  return p ? { ...p } : null;
}

// ─── Tax computation (all in paise, Math.floor per line) ─────────────────────

/**
 * Grand total for a parked bill — the same figure the cart sidebar shows:
 * bill discount pro-rated across lines on the pre-tax base, GST floored per
 * line (`computeLineGst`), then cash-rounded to the nearest rupee.
 */
function computeParkedTotal(
  lines: Pick<CartLineItem, 'productId' | 'lineSubtotal' | 'discountAmount' | 'gstRate'>[],
  billDiscount: BillDiscount | null,
  isInterState: boolean,
): number {
  const nets = lines.map((l) => Math.max(0, l.lineSubtotal - l.discountAmount));
  // A scoped voucher (C03) is spread over its matching lines only.
  const weights = lines.map((l, i) => (sharesBillDiscount(l.productId, billDiscount) ? nets[i] : 0));
  const taxableBase = weights.reduce((s, w) => s + w, 0);
  let totalDiscount = 0;
  if (billDiscount && taxableBase > 0) {
    const requested =
      billDiscount.type === 'PERCENTAGE'
        ? Math.floor((taxableBase * Math.max(0, Math.min(100, billDiscount.value))) / 100)
        : Math.max(0, Math.floor(billDiscount.value));
    totalDiscount = Math.min(requested, taxableBase);
  }
  const shares = weights.map((w) => (w <= 0 ? 0 : Math.floor((totalDiscount * w) / taxableBase)));
  const residual = totalDiscount - shares.reduce((s, v) => s + v, 0);
  if (residual > 0) {
    const idx = weights.findIndex((w) => w > 0);
    if (idx >= 0) shares[idx] += residual;
  }
  const total = lines.reduce((sum, l, i) => {
    const taxable = nets[i] - shares[i];
    const { cgst, sgst, igst } = computeLineGst(taxable, l.gstRate, isInterState, tenantCurrency);
    return sum + taxable + cgst + sgst + igst;
  }, 0);
  return roundBillTotal(total, cashRoundingRuleFor(tenantCurrency));
}

/** "Apple Watch SE" / "Apple Watch SE +2 more" — a glanceable hint for walk-in bills. */
function summarizeItems(items: Pick<CartLineItem, 'name'>[]): string | undefined {
  if (items.length === 0) return undefined;
  const rest = items.length - 1;
  return rest > 0 ? `${items[0].name} +${rest} more` : items[0].name;
}

function computeLineItem(
  item: Pick<CartLineItem, 'unitPrice' | 'quantity' | 'gstRate' | 'discountType' | 'discountValue'>,
  isInterState: boolean,
  membershipPct = 0,
): Pick<CartLineItem, 'lineSubtotal' | 'discountAmount' | 'cgst' | 'sgst' | 'igst' | 'taxAmount' | 'lineTotal'> {
  // Whole minor units even for a measured quantity (1.333 kg), as the API rounds.
  const lineSubtotal = Math.round(item.unitPrice * item.quantity);

  let discountAmount = 0;
  if (item.discountType === 'PERCENTAGE') {
    discountAmount = Math.floor((lineSubtotal * item.discountValue) / 100);
  } else if (item.discountType === 'FLAT') {
    discountAmount = Math.min(item.discountValue, lineSubtotal);
  } else if (membershipPct > 0) {
    // Auto membership tier discount — only on lines with no manual discount,
    // floored per line to match the backend's createBill auto-apply exactly.
    discountAmount = Math.floor((lineSubtotal * membershipPct) / 100);
  }

  const taxable = lineSubtotal - discountAmount;
  const { cgst, sgst, igst } = computeLineGst(taxable, item.gstRate, isInterState, tenantCurrency);
  const taxAmount = cgst + sgst + igst;
  const lineTotal = taxable + taxAmount;

  return { lineSubtotal, discountAmount, cgst, sgst, igst, taxAmount, lineTotal };
}

function recomputeItems(
  items: CartLineItem[],
  isInterState: boolean,
  membershipPct = 0,
): CartLineItem[] {
  return items.map((item) => ({
    ...item,
    ...computeLineItem(item, isInterState, membershipPct),
  }));
}

/**
 * The cart as checkout will price it: a line the live promotional rules
 * discount by more than its own discount is shown and totalled with the
 * promotion (largest wins, as on the server). Display only — the store's
 * lines, and what checkout sends, are untouched; the server applies the
 * promotion itself (QA 4.3.16).
 */
export function applyPromoPreview(
  items: CartLineItem[],
  promoByLine: Record<string, number>,
  isInterState: boolean,
): CartLineItem[] {
  return items.map((item) => {
    const promo = promoByLine[item.id] ?? 0;
    if (promo <= item.discountAmount) return item;
    return {
      ...item,
      ...computeLineItem({ ...item, discountType: 'FLAT', discountValue: promo }, isInterState),
    };
  });
}

// ─── Debounced persistence helper ────────────────────────────────────────────

let persistTimer: ReturnType<typeof setTimeout> | null = null;

function persistDraft(state: CartState) {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    persistTimer = null;
    // T2-012: an empty cart keeps no draft — otherwise every checkout leaves an
    // empty row behind that the logout guard counts as work to lose.
    if (state.items.length === 0) {
      offlineBillingService.removeDraft(state.currentDraftId).catch(() => undefined);
      return;
    }
    offlineBillingService
      .saveDraft({
        clientId: state.currentDraftId,
        items: state.items,
        customerId: state.selectedCustomer?.id ?? null,
        customerName: state.selectedCustomer?.name ?? null,
        customerPhone: (state.selectedCustomer as unknown as Record<string, unknown>)?.phone as string ?? null,
        locationId: useLocationStore.getState().currentLocationId,
        billMode: state.billMode,
        isInterState: state.isInterState,
        supplyOverride: state.supplyOverride,
        isParked: false,
        parkedReference: null,
        billDiscount: toDraftDiscount(state.billDiscount),
        pendingDiscountApproval: toDraftPendingApproval(state.pendingDiscountApproval),
        serverBillId: state.currentBillId,
      })
      .catch(() => {
        // IndexedDB write failed — non-critical, cart still in memory
      });
  }, 300);
}

// ─── Parking (gst-parked 120, owner decisions D1 / D2) ──────────────────────
//
// A parked bill lives on the server and belongs to the store: every till there
// lists it and any cashier may resume it. Offline, the cart parks on this till
// (`draft_bills`, is_parked = 1) and uploads when the till is back online. A
// return / quotation cart can't become a server bill — it stays on this till.

/** The fields that reset the cart once it has been parked. */
function emptiedCart(): Partial<CartState> {
  return {
    items: [],
    isInterState: defaultInterState(),
    supplyOverride: null,
    supplyOverrideApprovalToken: null,
    selectedCustomer: null,
    currentBillId: null,
    currentDraftId: crypto.randomUUID(),
    billDiscount: null,
    pendingDiscountApproval: null,
    membership: null,
    editingQuote: null,
    billingQuote: null,
    exchangeCredit: null,
  };
}

/**
 * Can the till reach the server? `?.`: unit tests that mock the network store
 * as a bare selector leave out getState — the browser's flag stands in.
 */
function isTillOnline(): boolean {
  return useNetworkStore.getState?.()?.isConnected ?? (typeof navigator === 'undefined' || navigator.onLine);
}

/** The signed-in user (stored by the auth store), for the offline park's row. */
function currentUserId(): string | null {
  try {
    const raw = localStorage.getItem('bbpos_user');
    const sub = raw ? (JSON.parse(raw) as { sub?: unknown }).sub : null;
    return typeof sub === 'string' ? sub : null;
  } catch {
    return null;
  }
}

function parkableFromState(state: CartState, locationId: string): ParkableCart {
  return {
    clientId: state.currentDraftId,
    serverBillId: state.currentBillId,
    locationId,
    items: state.items,
    customerId: state.selectedCustomer?.id ?? null,
    billDiscount: state.billDiscount,
    billDiscountOnServer: discountIsOnServer(state.billDiscount?.appliedBy),
    note: parkNote(state.items),
  };
}

/** The store to park at on the server, or null when this cart parks on the till. */
function serverParkLocation(state: CartState): string | null {
  const locationId = useLocationStore.getState().currentLocationId;
  if (!locationId || state.billMode !== 'sale' || state.pendingDiscountApproval) return null;
  return isTillOnline() ? locationId : null;
}

/**
 * Keep the cart on this till as a parked row — a sale cart waits for upload,
 * any other stays here. Its id is the cart's client id, so the upload's
 * create is keyed the same as an online attempt that got half way.
 */
async function parkStateLocally(
  state: CartState,
  reference: string,
  parkedTotal: number,
): Promise<ParkedBill> {
  const pendingDiscountApproval = toDraftPendingApproval(state.pendingDiscountApproval);
  await offlineBillingService.saveDraft({
    clientId: state.currentDraftId,
    items: state.items,
    customerId: state.selectedCustomer?.id ?? null,
    customerName: state.selectedCustomer?.name ?? null,
    customerPhone: (state.selectedCustomer as unknown as Record<string, unknown>)?.phone as string ?? null,
    locationId: useLocationStore.getState().currentLocationId,
    billMode: state.billMode,
    isInterState: state.isInterState,
    supplyOverride: state.supplyOverride,
    isParked: true,
    parkedReference: reference,
    billDiscount: toDraftDiscount(state.billDiscount),
    pendingDiscountApproval,
    serverBillId: state.currentBillId,
    userId: currentUserId(),
    parkedTotal,
  });
  const uploadable = isUploadableParkedDraft({
    is_parked: 1,
    bill_mode: state.billMode,
    pending_discount_approval: pendingDiscountApproval,
  } as DraftBill);
  return {
    id: state.currentDraftId,
    referenceNumber: reference,
    customerName: state.selectedCustomer?.name,
    total: parkedTotal,
    itemCount: state.items.reduce((sum, i) => sum + i.quantity, 0),
    parkedAt: new Date().toISOString(),
    summary: summarizeItems(state.items),
    serverBillId: state.currentBillId ?? undefined,
    localState: uploadable ? 'waiting' : 'device',
  };
}

/**
 * Park the cart wherever it can go: the server first, else this till.
 * `refuse`: a server refusal reaches the caller (a cashier's park, cart kept)
 * instead of falling back to the till (an auto-park, which must lose nothing).
 * Leaves the cart itself to the caller.
 */
async function parkState(
  state: CartState,
  set: StoreApi<CartState>['setState'],
  get: () => CartState,
  refuse: boolean,
  promoByLine?: Record<string, number>,
): Promise<ParkOutcome> {
  // A pending debounced write would turn the parked row back into the active cart.
  if (persistTimer) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }
  let current = state;
  // The cart's payable total, with the promotion the sidebar shows (GP2-001).
  const parkedTotal = computeParkedTotal(
    promoByLine ? applyPromoPreview(state.items, promoByLine, state.isInterState) : state.items,
    state.billDiscount,
    state.isInterState,
  );
  const locationId = serverParkLocation(state);
  if (locationId) {
    let createdId: string | null = null;
    try {
      const result = await parkOnServer(parkableFromState(state, locationId), (id) => {
        createdId = id;
      });
      offlineBillingService.removeDraft(state.currentDraftId).catch(() => undefined);
      // Listed at once; the refresh brings the server's row (who parked it).
      const listed: ParkedBill = {
        id: result.billId,
        referenceNumber: result.parkReference ?? '',
        customerName: state.selectedCustomer?.name,
        total: parkedTotal,
        itemCount: state.items.reduce((sum, i) => sum + i.quantity, 0),
        parkedAt: new Date().toISOString(),
        summary: summarizeItems(state.items),
        serverBillId: result.billId,
      };
      set((s) => ({ parkedBills: [listed, ...s.parkedBills.filter((b) => b.id !== listed.id)] }));
      void get().refreshParkedBills();
      return { where: 'server', reference: result.parkReference ?? '' };
    } catch (err) {
      if (refuse && !isOfflineParkError(err)) {
        persistDraft(get()); // the cart stays — keep its crash-recovery copy
        throw err;
      }
      // The upload finishes the draft this attempt created.
      if (createdId) current = { ...state, currentBillId: createdId };
    }
  }
  const reference = `PARK-${Date.now().toString(36).toUpperCase()}`;
  const parked = await parkStateLocally(current, reference, parkedTotal);
  set((s) => ({ parkedBills: [parked, ...s.parkedBills.filter((b) => b.id !== parked.id)] }));
  return { where: 'local', reference };
}

/**
 * Park the active cart (if it has lines) before another bill replaces it, so
 * nothing is lost. Resolves with the reference used, or null when the cart
 * was empty. Shared by `resumeBill`, `hydrateFromServerBill` and the quote
 * loaders. Never rejects for a server refusal — the cart parks on the till.
 */
async function autoParkCurrentCart(
  state: CartState,
  set: StoreApi<CartState>['setState'],
  get: () => CartState,
): Promise<string | null> {
  if (state.items.length === 0) {
    offlineBillingService.removeDraft(state.currentDraftId).catch(() => {});
    return null;
  }
  const outcome = await parkState(state, set, get, false);
  return outcome.reference;
}

/** A row of the store's server parked list. */
function serverParkedEntry(row: ServerParkedBill): ParkedBill {
  return {
    id: row.id,
    referenceNumber: row.park_reference ?? row.bill_number,
    customerName: row.customer_name ?? undefined,
    // A parked bill has no cash round-off until it completes; show the cart's figure.
    total: roundBillTotal(Number(row.grand_total ?? 0), cashRoundingRuleFor(tenantCurrency)),
    itemCount: Number(row.item_count ?? 0),
    parkedAt: row.parked_at,
    summary: row.note ?? undefined,
    serverBillId: row.id,
    parkedByName: row.parked_by_name ?? undefined,
  };
}

/** A row parked on this till. */
function localParkedEntry(draft: DraftBill): ParkedBill {
  const isInterState = interStateFor(draft.supply_override);
  const items = Array.isArray(draft.items) ? draft.items : [];
  const lines = items.map((i) => ({
    ...computeLineItem(i, isInterState),
    productId: i.productId,
    gstRate: i.gstRate,
  }));
  return {
    id: draft.client_id,
    referenceNumber: draft.parked_reference ?? '',
    customerName: draft.customer_name ?? undefined,
    total:
      draft.parked_total ??
      computeParkedTotal(lines, fromDraftDiscount(draft.bill_discount), isInterState),
    itemCount: items.reduce((s, i) => s + i.quantity, 0),
    parkedAt: draft.created_at,
    summary: summarizeItems(items),
    serverBillId: draft.server_bill_id ?? undefined,
    localState: isUploadableParkedDraft(draft) ? 'waiting' : 'device',
    ...(draft.upload_error ? { uploadError: draft.upload_error } : {}),
  };
}

/** The last refresh wins when two overlap (a socket event and a focus). */
let parkedRefreshSeq = 0;

// ─── GST supply type (gst-parked 080, owner decision D7) ───────────────────
//
// Each store's CGST + SGST vs IGST comes from the shared GST resolver, sent on
// /auth/me as `locations[].supply_type` (the retired "Default Transaction
// Type" setting is no longer read). Held module-level, set by the auth store,
// because the store's initial state is built before any profile exists. A
// cart with no override follows the current store, also when the store
// changes; the cashier's switch is an override sent with the bill.

export type SupplyOverride = 'INTRA' | 'INTER';

let locationSupplyTypes = new Map<string, SupplyOverride>();

export function setLocationSupplyTypes(
  locations: ReadonlyArray<{ id: string; supply_type?: SupplyOverride | null }> | null | undefined,
): void {
  locationSupplyTypes = new Map(
    (locations ?? []).flatMap((l) => (l.supply_type ? [[l.id, l.supply_type] as const] : [])),
  );
  applySupplyDefault();
}

/** The current store's split: IGST only when the resolver says INTER (D4: unknown → same-state). */
export function defaultInterState(): boolean {
  const locationId = useLocationStore.getState().currentLocationId;
  return !!locationId && locationSupplyTypes.get(locationId) === 'INTER';
}

/** A restored draft's split: its saved override, else the store's own (never the stale stored flag). */
function interStateFor(override: SupplyOverride | null | undefined): boolean {
  return override ? override === 'INTER' : defaultInterState();
}

/** Re-seed a cart that has no override from the current store's supply type. */
function applySupplyDefault(): void {
  const state = useCartStore.getState();
  if (state.supplyOverride) return;
  const interState = defaultInterState();
  if (state.isInterState === interState) return;
  useCartStore.setState({
    isInterState: interState,
    items: recomputeItems(state.items, interState, state.membership?.discountPct ?? 0),
  });
}

// ─── Tenant currency ─────────────────────────────────────────────────────────
//
// The tenant's immutable currency drives the tax regime (India floor vs GCC
// round-half-up) and the cash-rounding subunit of every line recompute, so the
// online cart agrees with the offline path and the server (QA 3.10.21). Held
// module-level like the inter-state default: set by the auth store after login.

let tenantCurrency: CurrencyCode = 'INR';

export function setTenantCurrency(value: string | null | undefined): void {
  const code = (value ?? '').toUpperCase();
  const next = (SUPPORTED_CURRENCIES as readonly string[]).includes(code) ? (code as CurrencyCode) : 'INR';
  if (next === tenantCurrency) return;
  tenantCurrency = next;
  const state = useCartStore.getState();
  if (state.items.length > 0) {
    useCartStore.setState({
      items: recomputeItems(state.items, state.isInterState, state.membership?.discountPct ?? 0),
    });
  }
}

/**
 * gst-parked 090 (D5): "GST registered" from /auth/me. Not registered (India)
 * ⇒ every line's tax is 0 (`computeLineGst`); an open cart is re-taxed when
 * the answer changes.
 */
export function setCartGstRegistration(
  registered: boolean | null | undefined,
  currency: string | null | undefined,
): void {
  const before = gstWaived();
  setGstRegistration(registered, currency);
  if (gstWaived() === before) return;
  const state = useCartStore.getState();
  if (state.items.length > 0) {
    useCartStore.setState({
      items: recomputeItems(state.items, state.isInterState, state.membership?.discountPct ?? 0),
    });
  }
}

// ─── Store ───────────────────────────────────────────────────────────────────

export const useCartStore = create<CartState>()(devtools((set, get) => ({
  items: [],
  billMode: 'sale',
  selectedCustomer: null,
  parkedBills: [],
  currentBillId: null,
  currentDraftId: crypto.randomUUID(),
  // No store supply types exist before login; setLocationSupplyTypes re-seeds.
  isInterState: false,
  supplyOverride: null,
  supplyOverrideApprovalToken: null,
  isHydrated: false,
  billDiscount: null,
  pendingDiscountApproval: null,
  membership: null,
  editingQuote: null,
  billingQuote: null,
  exchangeCredit: null,
  ingredientAvailability: {},
  lastAddedItemId: null,

  addItem(product, quantity = 1) {
    const state = get();
    const existing = state.items.find((i) => i.productId === product.id);

    // #308: refuse to exceed recipe availability so an ingredient shortage
    // surfaces at add-to-cart, not at payment. Enforced here (not only in the
    // grid handler) so barcode/hardware-scan/image-match paths can't bypass it.
    const limit = state.ingredientAvailability[product.id];
    if (limit && (existing?.quantity ?? 0) + quantity > limit.available) {
      return false;
    }

    if (existing) {
      const updated = state.items.map((i) =>
        i.productId === product.id ? { ...i, quantity: i.quantity + quantity } : i,
      );
      // Re-adding an existing dish bumps its quantity in place rather than
      // appending, so the row to highlight is that one — not the last in the
      // list, which is the common case and would otherwise flash nothing.
      set({
        items: recomputeItems(updated, state.isInterState, state.membership?.discountPct ?? 0),
        lastAddedItemId: existing.id,
      });
    } else {
      const newItem: CartLineItem = {
        id: crypto.randomUUID(),
        productId: product.id,
        name: product.name,
        sku: product.sku,
        unit: product.unit,
        quantity,
        unitPrice: product.price,
        listPrice: product.price,
        unitCost: product.cost,
        gstRate: product.gst_rate,
        hsnCode: product.hsn_code,
        discountType: null,
        discountValue: 0,
        // Computed — will be filled by recompute
        discountAmount: 0,
        lineSubtotal: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        taxAmount: 0,
        lineTotal: 0,
      };

      set({
        items: recomputeItems([...state.items, newItem], state.isInterState, state.membership?.discountPct ?? 0),
        lastAddedItemId: newItem.id,
      });
    }
    persistDraft(get());
    return true;
  },

  setIngredientAvailability(map) {
    set({ ingredientAvailability: map });
  },

  removeItem(itemId) {
    set((s) => {
      const items = s.items.filter((i) => i.id !== itemId);
      // Emptying the cart ends the sale: a bill discount (or its pending
      // approval), a quote link or exchange credit must not carry over,
      // hidden, to the next sale. The customer stays for the next item.
      return items.length === 0
        ? {
            items,
            billDiscount: null,
            pendingDiscountApproval: null,
            editingQuote: null,
            billingQuote: null,
            exchangeCredit: null,
          }
        : { items };
    });
    persistDraft(get());
  },

  updateQuantity(itemId, qty) {
    if (qty <= 0) {
      get().removeItem(itemId);
      return;
    }
    const state = get();
    const updated = state.items.map((i) =>
      // Serials are one-per-unit: dropping the quantity must drop the extras,
      // or checkout would submit more serials than units and be rejected.
      i.id === itemId
        ? { ...i, quantity: qty, ...(i.serials ? { serials: i.serials.slice(0, qty) } : {}) }
        : i,
    );
    set({ items: recomputeItems(updated, state.isInterState, state.membership?.discountPct ?? 0) });
    persistDraft(get());
  },

  setLineSerials(itemId, serials) {
    const state = get();
    const updated = state.items.map((i) =>
      i.id === itemId ? { ...i, serials } : i,
    );
    set({ items: recomputeItems(updated, state.isInterState, state.membership?.discountPct ?? 0) });
    persistDraft(get());
  },

  setLineModifiers(itemId, unitPrice, modifiers) {
    const state = get();
    const updated = state.items.map((i) =>
      i.id === itemId ? { ...i, unitPrice, modifiers } : i,
    );
    set({ items: recomputeItems(updated, state.isInterState, state.membership?.discountPct ?? 0) });
    persistDraft(get());
  },

  repriceLines(changes) {
    const state = get();
    const priceOf = new Map(changes.map((c) => [c.productId, c.price]));
    const updated = state.items.map((i) => {
      const price = priceOf.get(i.productId);
      if (price === undefined) return i;
      const surcharge = i.unitPrice - (i.listPrice ?? i.unitPrice);
      return { ...i, listPrice: price, unitPrice: price + surcharge };
    });
    set({ items: recomputeItems(updated, state.isInterState, state.membership?.discountPct ?? 0) });
    persistDraft(get());
  },

  applyItemDiscount(itemId, type, value) {
    const state = get();
    const updated = state.items.map((i) =>
      i.id === itemId ? { ...i, discountType: type, discountValue: value } : i,
    );
    set({ items: recomputeItems(updated, state.isInterState, state.membership?.discountPct ?? 0) });
    persistDraft(get());
  },

  clearItemDiscount(itemId) {
    const state = get();
    const updated = state.items.map((i) =>
      i.id === itemId ? { ...i, discountType: null, discountValue: 0 } : i,
    );
    set({ items: recomputeItems(updated, state.isInterState, state.membership?.discountPct ?? 0) });
    persistDraft(get());
  },

  applyBillDiscount(discount) {
    // Belt-and-braces validation — the modal validates first, this is the
    // defensive layer for programmatic callers and tests.
    if (!discount.reason || discount.reason.trim().length < 3) {
      throw new Error('Bill discount requires a reason (min 3 characters)');
    }
    if (!Number.isFinite(discount.value) || discount.value <= 0) {
      throw new Error('Bill discount value must be a positive number');
    }
    if (discount.type === 'PERCENTAGE' && discount.value > 100) {
      throw new Error('Bill discount percentage cannot exceed 100');
    }
    if (discount.type !== 'PERCENTAGE' && discount.type !== 'FLAT') {
      throw new Error('Bill discount type must be PERCENTAGE or FLAT');
    }
    set({ billDiscount: { ...discount, appliedAt: Date.now() } });
    persistDraft(get());
  },

  clearBillDiscount() {
    set({ billDiscount: null });
    persistDraft(get());
  },

  setPendingDiscountApproval(approval) {
    set({ pendingDiscountApproval: { ...approval, requestedAt: approval.requestedAt || Date.now() } });
    persistDraft(get());
  },

  setCurrentBillId(billId) {
    set({ currentBillId: billId });
    persistDraft(get());
  },

  clearPendingDiscountApproval() {
    set({ pendingDiscountApproval: null });
    persistDraft(get());
  },

  resolvePendingDiscountApproval(result, discount) {
    if (result === 'approved' && discount) {
      // Apply the approved discount and clear pending state
      set({
        billDiscount: { ...discount, appliedBy: APPROVED_DISCOUNT_APPLIED_BY, appliedAt: Date.now() },
        pendingDiscountApproval: null,
      });
    } else {
      // Rejected — just clear pending state
      set({ pendingDiscountApproval: null });
    }
    persistDraft(get());
  },

  setCustomer(customer) {
    const state = get();
    // Drop any prior membership when the customer changes/clears — the
    // BillingPage effect re-fetches and re-applies for the new customer.
    const customerChanged = state.selectedCustomer?.id !== customer?.id;
    if (customerChanged && state.membership) {
      set({
        selectedCustomer: customer,
        membership: null,
        items: recomputeItems(state.items, state.isInterState, 0),
      });
    } else {
      set({ selectedCustomer: customer });
    }
    persistDraft(get());
  },

  setMembership(membership) {
    const state = get();
    const pct = membership?.discountPct ?? 0;
    // Resumed lines carry the tier discount as a frozen FLAT amount. When it
    // matches exactly the tier share for the line, make it live again so it
    // re-scales with quantity (and checkout sends the right figure). A FLAT
    // that doesn't match is a genuine manual discount and is left alone.
    const items =
      pct > 0
        ? state.items.map((item) =>
            item.fromServer &&
            item.discountType === 'FLAT' &&
            item.discountValue === Math.floor((item.unitPrice * item.quantity * pct) / 100)
              ? { ...item, discountType: null, discountValue: 0 }
              : item,
          )
        : state.items;
    set({
      membership,
      items: recomputeItems(items, state.isInterState, pct),
    });
    persistDraft(get());
  },

  setBillMode(mode) {
    set({ billMode: mode });
    persistDraft(get());
  },

  setIsInterState(value, approvalToken) {
    const state = get();
    // Switching back to the store's own split clears the override: nothing is
    // sent and the server decides, as for a bill nobody switched.
    const supplyOverride: SupplyOverride | null =
      value === defaultInterState() ? null : value ? 'INTER' : 'INTRA';
    set({
      isInterState: value,
      supplyOverride,
      supplyOverrideApprovalToken: supplyOverride ? approvalToken ?? null : null,
      items: recomputeItems(state.items, value, state.membership?.discountPct ?? 0),
    });
    persistDraft(get());
  },

  clearCart() {
    const state = get();
    // Cancel any pending debounced write
    if (persistTimer) {
      clearTimeout(persistTimer);
      persistTimer = null;
    }
    // Remove old draft from IndexedDB
    offlineBillingService.removeDraft(state.currentDraftId).catch(() => {});
    set({
      items: [],
      selectedCustomer: null,
      currentBillId: null,
      currentDraftId: crypto.randomUUID(),
      billMode: 'sale',
      isInterState: defaultInterState(),
      supplyOverride: null,
      supplyOverrideApprovalToken: null,
      billDiscount: null,
      pendingDiscountApproval: null,
      membership: null,
      editingQuote: null,
      billingQuote: null,
      exchangeCredit: null,
    });
  },

  async parkCurrentBill(promoByLine) {
    const state = get();
    if (state.items.length === 0) return null;
    // The server bill is held for the manager's answer (not a DRAFT), so it
    // can't be parked, and a park on the till could never upload.
    if (state.pendingDiscountApproval && state.billMode === 'sale') {
      throw new Error(PARK_PENDING_APPROVAL_MSG);
    }
    const outcome = await parkState(state, set, get, true, promoByLine);
    set(emptiedCart());
    return outcome;
  },

  async resumeBill(_billId) {
    // Auto-park current bill if it has items (prevents data loss)
    const autoParkedReference = await autoParkCurrentCart(get(), set, get);
    set({ editingQuote: null, billingQuote: null, exchangeCredit: null });
    const parkedEntry = get().parkedBills.find((b) => b.id === _billId);

    // Load parked bill items from IndexedDB
    const draft = await offlineBillingService.loadDraft(_billId);

    if (draft && draft.items.length > 0) {
      const supplyOverride = draft.supply_override ?? null;
      const isInterState = interStateFor(supplyOverride);
      const cartItems: CartLineItem[] = draft.items.map((item) => ({
        ...item,
        discountAmount: 0,
        lineSubtotal: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        taxAmount: 0,
        lineTotal: 0,
      }));

      const resumedDiscount = fromDraftDiscount(draft.bill_discount);
      const resumedPendingApproval = fromDraftPendingApproval(draft.pending_discount_approval);

      set((s) => ({
        parkedBills: s.parkedBills.filter((b) => b.id !== _billId),
        // Only a bill that exists on the server carries an id here; a local
        // parked cart has none until payment creates the bill.
        currentBillId: draft.server_bill_id ?? null,
        currentDraftId: _billId,
        items: recomputeItems(cartItems, isInterState),
        isInterState,
        supplyOverride,
        supplyOverrideApprovalToken: null,
        billMode: toBillMode(draft.bill_mode), // a pre-T08 'credit' draft resumes as a sale
        selectedCustomer: draft.customer_id
          ? { id: draft.customer_id, name: draft.customer_name ?? '', phone: draft.customer_phone ?? '' }
          : null,
        billDiscount: resumedDiscount,
        pendingDiscountApproval: resumedPendingApproval,
        // Re-fetched by the BillingPage effect once the resumed customer loads.
        membership: null,
      }));

      // Re-save as active (not parked) draft
      offlineBillingService
        .saveDraft({
          clientId: _billId,
          items: draft.items.map((i) => ({
            ...i,
            discountAmount: 0,
            lineSubtotal: 0,
            cgst: 0,
            sgst: 0,
            igst: 0,
            taxAmount: 0,
            lineTotal: 0,
          })),
          customerId: draft.customer_id,
          customerName: draft.customer_name,
          customerPhone: draft.customer_phone,
          locationId: draft.location_id,
          billMode: draft.bill_mode,
          isInterState,
          supplyOverride,
          isParked: false,
          parkedReference: null,
          billDiscount: draft.bill_discount ?? null,
          pendingDiscountApproval: draft.pending_discount_approval ?? null,
          serverBillId: draft.server_bill_id ?? null,
        })
        .catch(() => {});
    } else {
      // Fallback: no draft found, just remove from parked list
      set((s) => ({
        parkedBills: s.parkedBills.filter((b) => b.id !== _billId),
        currentBillId: parkedEntry?.serverBillId ?? null,
      }));
    }
    return autoParkedReference;
  },

  async hydrateFromServerBill(bill, units) {
    const autoParkedReference = await autoParkCurrentCart(get(), set, get);
    const isInterState = defaultInterState();

    const cartItems: CartLineItem[] = bill.items.map((item) => {
      // BIGINT columns arrive as strings from the API.
      const lineDiscount = Number(item.line_discount ?? 0);
      return {
        id: crypto.randomUUID(),
        fromServer: true,
        productId: item.product_id,
        name: item.product_name,
        sku: item.product_sku,
        unit: units[item.product_id] ?? item.unit ?? 'PCS',
        quantity: Number(item.quantity),
        unitPrice: Number(item.unit_price),
        unitCost: Number(item.unit_cost),
        gstRate: Number(item.gst_rate),
        hsnCode: item.hsn_code ?? undefined,
        // The server keeps only the collapsed paise amount per line, so a
        // percentage discount comes back as its FLAT equivalent.
        discountType: lineDiscount > 0 ? 'FLAT' : null,
        discountValue: lineDiscount,
        discountAmount: 0,
        lineSubtotal: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        taxAmount: 0,
        lineTotal: 0,
      };
    });

    // Bill-level discount: the server keeps type/value/reason in
    // bill_discounts (GET /bills/:id → discount_details). Older drafts without
    // a row fall back to the collapsed figure (discount_total minus what the
    // lines carry) as a FLAT amount.
    const details = bill.discount_details;
    const lineDiscounts = cartItems.reduce((sum, i) => sum + i.discountValue, 0);
    const billLevel = Number(bill.discount_total ?? 0) - lineDiscounts;
    const billDiscount: BillDiscount | null = details
      ? {
          type: details.type,
          // Server value is basis points for PERCENTAGE; the cart keeps whole percent.
          value: details.type === 'PERCENTAGE' ? Number(details.value) / 100 : Number(details.value),
          reason: details.reason,
          appliedBy: RESUMED_DISCOUNT_APPLIED_BY,
          appliedAt: Date.now(),
          ...(details.voucher_code ? { voucherCode: details.voucher_code } : {}),
        }
      : billLevel > 0
        ? {
            type: 'FLAT',
            value: billLevel,
            reason: 'Discount carried over from the saved draft',
            appliedBy: RESUMED_DISCOUNT_APPLIED_BY,
            appliedAt: Date.now(),
          }
        : null;

    const selectedCustomer: CustomerLookupResult | null = bill.customer_id
      ? { id: bill.customer_id, name: bill.customer_name ?? '', phone: bill.customer_phone ?? '' }
      : null;

    // A locally parked copy of this same server draft is superseded by the
    // server's version — drop it (and its IndexedDB row) so it can't be
    // resumed a second time.
    const stale = get().parkedBills.filter((b) => b.serverBillId === bill.id && b.localState);
    stale.forEach((b) => offlineBillingService.removeDraft(b.id).catch(() => {}));

    set((s) => ({
      parkedBills: s.parkedBills.filter((b) => b.serverBillId !== bill.id),
      currentBillId: bill.id,
      currentDraftId: bill.id,
      items: recomputeItems(cartItems, isInterState),
      isInterState,
      supplyOverride: null,
      supplyOverrideApprovalToken: null,
      billMode: 'sale',
      selectedCustomer,
      billDiscount,
      pendingDiscountApproval: null,
      // Re-fetched by the BillingPage effect once the resumed customer loads.
      membership: null,
      editingQuote: null,
      billingQuote: null,
      exchangeCredit: bill.exchange_credit
        ? { amount: Number(bill.exchange_credit.amount), creditNoteNumber: bill.exchange_credit.bill_number }
        : null,
      lastAddedItemId: null,
    }));

    offlineBillingService
      .saveDraft({
        clientId: bill.id,
        items: cartItems,
        customerId: selectedCustomer?.id ?? null,
        customerName: selectedCustomer?.name ?? null,
        customerPhone: selectedCustomer?.phone ?? null,
        locationId: bill.location_id,
        billMode: 'sale',
        isInterState,
        supplyOverride: null,
        isParked: false,
        parkedReference: null,
        billDiscount: toDraftDiscount(billDiscount),
        pendingDiscountApproval: null,
        serverBillId: bill.id,
      })
      .catch(() => {});

    return autoParkedReference;
  },

  async loadQuoteForEdit(quote, units) {
    const autoParkedReference = await autoParkCurrentCart(get(), set, get);
    const isInterState = defaultInterState();
    const cartItems: CartLineItem[] = quote.items.map((item) => {
      const lineDiscount = Number(item.line_discount ?? 0);
      return {
        id: crypto.randomUUID(),
        fromServer: true,
        productId: item.product_id,
        name: item.product_name,
        sku: item.product_sku ?? '',
        unit: units[item.product_id] ?? 'PCS',
        quantity: Number(item.quantity),
        unitPrice: Number(item.unit_price),
        // A quote never deducts stock, so its lines carry no cost.
        unitCost: 0,
        gstRate: Number(item.gst_rate),
        discountType: lineDiscount > 0 ? 'FLAT' : null,
        discountValue: lineDiscount,
        discountAmount: 0,
        lineSubtotal: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        taxAmount: 0,
        lineTotal: 0,
      };
    });
    set({
      items: recomputeItems(cartItems, isInterState),
      isInterState,
      supplyOverride: null,
      supplyOverrideApprovalToken: null,
      billMode: 'quotation',
      selectedCustomer: quote.customer_id
        ? { id: quote.customer_id, name: quote.customer_name ?? '', phone: quote.customer_phone ?? '' }
        : null,
      currentBillId: null,
      currentDraftId: crypto.randomUUID(),
      billDiscount: null,
      pendingDiscountApproval: null,
      membership: null,
      editingQuote: { id: quote.id, quoteNumber: quote.quote_number, locationId: quote.location_id },
      billingQuote: null,
      exchangeCredit: null,
      lastAddedItemId: null,
    });
    return autoParkedReference;
  },

  async loadQuoteForBilling(quote, units) {
    const autoParkedReference = await autoParkCurrentCart(get(), set, get);
    const isInterState = defaultInterState();
    const cartItems: CartLineItem[] = quote.items.map((item) => {
      const lineDiscount = Number(item.line_discount ?? 0);
      // The bill is priced from the catalogue, so the cart shows that price.
      const price = Number(item.current_price ?? item.unit_price);
      return {
        id: crypto.randomUUID(),
        productId: item.product_id,
        name: item.product_name,
        sku: item.product_sku ?? '',
        unit: units[item.product_id] ?? 'PCS',
        quantity: Number(item.quantity),
        unitPrice: price,
        listPrice: price,
        unitCost: 0,
        gstRate: Number(item.gst_rate),
        discountType: lineDiscount > 0 ? 'FLAT' : null,
        discountValue: lineDiscount,
        discountAmount: 0,
        lineSubtotal: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        taxAmount: 0,
        lineTotal: 0,
      };
    });
    set({
      items: recomputeItems(cartItems, isInterState),
      isInterState,
      supplyOverride: null,
      supplyOverrideApprovalToken: null,
      billMode: 'sale',
      selectedCustomer: quote.customer_id
        ? { id: quote.customer_id, name: quote.customer_name ?? '', phone: quote.customer_phone ?? '' }
        : null,
      currentBillId: null,
      currentDraftId: crypto.randomUUID(),
      billDiscount: null,
      pendingDiscountApproval: null,
      membership: null,
      editingQuote: null,
      billingQuote: { id: quote.id, quoteNumber: quote.quote_number, advancePaid: Number(quote.advance_paid ?? 0) },
      exchangeCredit: null,
      lastAddedItemId: null,
    });
    return autoParkedReference;
  },

  async discardParkedBill(billId) {
    const parked = get().parkedBills.find((b) => b.id === billId);
    if (parked && !parked.localState) {
      // gst-parked 120: the store's parked bill — voiding it drops it from
      // every till (bill:voided). A refusal (a cashier voiding another
      // cashier's park) reaches the caller and the list stays.
      await billingService.voidBill(billId, 'Parked bill discarded');
    } else {
      offlineBillingService.removeDraft(billId).catch(() => {});
      // A cart resumed from a numbered server draft leaves that DRAFT behind
      // in Bill History unless we void it too (best-effort, offline-safe).
      if (parked?.serverBillId) {
        billingService.voidBill(parked.serverBillId).catch(() => {
          /* offline, already voided, or completed elsewhere — nothing to do */
        });
      }
    }
    set((s) => ({
      parkedBills: s.parkedBills.filter((b) => b.id !== billId),
    }));
  },

  async refreshParkedBills() {
    const seq = ++parkedRefreshSeq;
    const locationId = useLocationStore.getState().currentLocationId;
    let local: DraftBill[] = [];
    try {
      local = await offlineBillingService.loadParkedDrafts(locationId ?? undefined);
    } catch {
      /* IndexedDB unavailable — the server list still shows */
    }
    let server: ServerParkedBill[] | null = null;
    if (locationId && isTillOnline()) {
      try {
        server = await billingService.getParkedBills(locationId);
      } catch {
        server = null; // keep the last list we had
      }
    }
    if (seq !== parkedRefreshSeq) return;
    if (server) {
      // A park whose upload landed though its reply was lost: the server
      // holds it now, so the till's copy goes.
      const onServer = new Set(server.flatMap((r) => [r.id, r.client_id ?? '']));
      const landed = local.filter(
        (d) => isUploadableParkedDraft(d) && (onServer.has(d.client_id) || (!!d.server_bill_id && onServer.has(d.server_bill_id))),
      );
      landed.forEach((d) => offlineBillingService.removeDraft(d.client_id).catch(() => undefined));
      local = local.filter((d) => !landed.includes(d));
    }
    const serverEntries = server
      ? server.map(serverParkedEntry)
      : get().parkedBills.filter((b) => !b.localState);
    const next = [...local.map(localParkedEntry), ...serverEntries];
    // Most refreshes (focus, another store's event) change nothing — keep
    // the same list so nothing re-renders.
    if (JSON.stringify(next) !== JSON.stringify(get().parkedBills)) set({ parkedBills: next });
  },

  async syncParkedBills() {
    if (isTillOnline()) {
      try {
        await pushLocalParkedBills();
      } catch {
        /* retried on the next reconnect, logout or visit */
      }
    }
    await get().refreshParkedBills();
  },

  resetAll() {
    if (persistTimer) {
      clearTimeout(persistTimer);
      persistTimer = null;
    }
    offlineBillingService.clearAllDrafts().catch(() => {});
    set({
      items: [],
      parkedBills: [],
      selectedCustomer: null,
      currentBillId: null,
      currentDraftId: crypto.randomUUID(),
      billMode: 'sale',
      isInterState: defaultInterState(),
      supplyOverride: null,
      supplyOverrideApprovalToken: null,
      billDiscount: null,
      pendingDiscountApproval: null,
      membership: null,
      editingQuote: null,
      billingQuote: null,
      exchangeCredit: null,
      ingredientAvailability: {},
      lastAddedItemId: null,
    });
  },

  async hydrateFromIndexedDB() {
    try {
      // Restore active draft
      const activeDraft = await offlineBillingService.loadActiveDraft();
      if (activeDraft && Array.isArray(activeDraft.items) && activeDraft.items.length > 0) {
        const supplyOverride = activeDraft.supply_override ?? null;
        const isInterState = interStateFor(supplyOverride);
        const cartItems: CartLineItem[] = activeDraft.items.map((item) => ({
          ...item,
          discountAmount: 0,
          lineSubtotal: 0,
          cgst: 0,
          sgst: 0,
          igst: 0,
          taxAmount: 0,
          lineTotal: 0,
        }));
        set({
          items: recomputeItems(cartItems, isInterState),
          currentDraftId: activeDraft.client_id,
          currentBillId: activeDraft.server_bill_id ?? null,
          billMode: toBillMode(activeDraft.bill_mode), // a pre-T08 'credit' draft resumes as a sale
          isInterState,
          supplyOverride,
          supplyOverrideApprovalToken: null,
          selectedCustomer: activeDraft.customer_id
            ? { id: activeDraft.customer_id, name: activeDraft.customer_name ?? '', phone: activeDraft.customer_phone ?? '' }
            : null,
          billDiscount: fromDraftDiscount(activeDraft.bill_discount),
          pendingDiscountApproval: fromDraftPendingApproval(activeDraft.pending_discount_approval),
        });
      }

      // Restore this till's parks for the current store (gst-parked 120);
      // the store's server list joins them on the billing page's refresh.
      const locationId = useLocationStore.getState().currentLocationId;
      const parkedDrafts = await offlineBillingService.loadParkedDrafts(locationId ?? undefined);
      if (parkedDrafts.length > 0) {
        set({ parkedBills: parkedDrafts.map(localParkedEntry) });
      }
    } catch {
      // IndexedDB not available — continue with empty cart
    }
    set({ isHydrated: true });
  },
}), { name: 'cart-store' }));

// A cart with no override follows the store it bills at (gst-parked 080).
// `?.`: unit tests that mock the location store often leave out subscribe.
useLocationStore.subscribe?.((state, prev) => {
  if (state.currentLocationId !== prev.currentLocationId) applySupplyDefault();
});

// ─── Selectors ───────────────────────────────────────────────────────────────

export const selectSubtotal = (s: CartState) =>
  s.items.reduce((sum, i) => sum + i.lineSubtotal, 0);

export const selectTotalDiscount = (s: CartState) =>
  s.items.reduce((sum, i) => sum + i.discountAmount, 0);

export const selectCgstTotal = (s: CartState) =>
  s.items.reduce((sum, i) => sum + i.cgst, 0);

export const selectSgstTotal = (s: CartState) =>
  s.items.reduce((sum, i) => sum + i.sgst, 0);

export const selectIgstTotal = (s: CartState) =>
  s.items.reduce((sum, i) => sum + i.igst, 0);

export const selectRoundOff = (s: CartState) => {
  const raw = s.items.reduce((sum, i) => sum + i.lineTotal, 0);
  return roundBillTotal(raw, cashRoundingRuleFor(tenantCurrency)) - raw; // the till's cash rounding rule (T07)
};

export const selectGrandTotal = (s: CartState) => {
  const raw = s.items.reduce((sum, i) => sum + i.lineTotal, 0);
  return roundBillTotal(raw, cashRoundingRuleFor(tenantCurrency)); // the till's cash rounding rule (T07)
};

/**
 * gst-parked D7: the cart's IGST switch as the complete payload sends it — the
 * override and any manager approval token; empty when nobody switched, so the
 * server stamps the store's own split.
 */
export const selectSupplyOverridePayload = (
  s: Pick<CartState, 'supplyOverride' | 'supplyOverrideApprovalToken'>,
): { supply_override?: SupplyOverride; supply_override_approval_token?: string } =>
  s.supplyOverride
    ? {
        supply_override: s.supplyOverride,
        ...(s.supplyOverrideApprovalToken
          ? { supply_override_approval_token: s.supplyOverrideApprovalToken }
          : {}),
      }
    : {};

export const selectItemCount = (s: CartState) =>
  s.items.reduce((sum, i) => sum + i.quantity, 0);
